// TopClientsBarChart.tsx
// Gráfico de barras horizontais para top clientes (estilo sistema-producao)

import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList
} from 'recharts';

interface ClientData {
    Nome_Cliente_Completo: string;
    ClientId?: number;
    totalVenda: number;
    totalMargem: number;
}

interface TopClientsBarChartProps {
    data: ClientData[];
    totalVenda: number;
    onClientClick?: (clientName: string, clientId?: number) => void;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short'
    }).format(value);
};

const wrapText = (text: any, limit: number): string[] => {
    const str = String(text ?? '');
    if (!str) return [''];
    const words = str.split(/([ /])/);
    const lines: string[] = [];
    let currentLine = "";
    words.forEach((part: string) => {
        if (!part) return;
        if ((currentLine + part).length > limit && currentLine !== "") {
            lines.push(currentLine.trim());
            currentLine = part === " " ? "" : part;
        } else {
            currentLine += part;
        }
    });
    if (currentLine.trim()) lines.push(currentLine.trim());
    if (lines.length === 0) lines.push(str);
    return lines;
};

const BarTick = (props: any) => {
    const { x, y, payload } = props;
    const label = String(payload.value ?? '');
    const maxLen = 16;
    const display = label.length > maxLen ? label.substring(0, maxLen - 1) + '...' : label;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={0}
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill="var(--text-primary, #111827)"
                style={{ textTransform: 'uppercase' }}
            >
                {display}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const nome = d.Nome_Cliente_Completo || '';
    return (
        <div style={{
            background: 'var(--bg-widget)', borderRadius: 10, padding: '8px 12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '1px solid var(--border-primary)', fontSize: 12, maxWidth: 260
        }}>
            {nome && <p style={{ fontWeight: 700, color: 'var(--text-primary, #111827)', margin: '0 0 4px', fontSize: 13 }}>{nome}</p>}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}>Vendas</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary, #111827)' }}>{formatCurrency(d.totalVenda)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}>Margem</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary, #111827)' }}>{formatCurrency(d.totalMargem)}</span>
            </div>
        </div>
    );
};

const TOP_N = 10;

const TopClientsBarChart: React.FC<TopClientsBarChartProps> = ({ data, totalVenda, onClientClick }) => {
    if (!data?.length) {
        return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontWeight: 600, fontSize: 13 }}>Nenhum cliente encontrado.</div>;
    }

    const chartData = [...data]
        .sort((a, b) => b.totalVenda - a.totalVenda)
        .slice(0, TOP_N);

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 130, left: 0, bottom: 4 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" hide domain={[0, (max: number) => max * 1.35]} />
                <YAxis dataKey="Nome_Cliente_Completo" type="category" axisLine={false} tickLine={false} width={160} tick={<BarTick />} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar
                    dataKey="totalVenda"
                    fill="var(--theme-accent, #1e293b)"
                    radius={[0, 0, 0, 0]}
                    barSize={16}
                    style={{ cursor: 'pointer' }}
                    onClick={(data: any) => onClientClick?.(data.payload.Nome_Cliente_Completo, data.payload.ClientId)}
                >
                    <LabelList
                        dataKey="totalVenda"
                        position="right"
                        content={({ x, y, width, height, value }: any) => (
                            <text
                                x={(x as number) + (width as number) + 8}
                                y={(y as number) + (height as number) / 2}
                                textAnchor="start"
                                dominantBaseline="central"
                                fontSize={13}
                                fontWeight={700}
                                fill="var(--text-primary, #111827)"
                                style={{ pointerEvents: 'none' }}
                            >
                                {formatCurrency(value)}
                            </text>
                        )}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default TopClientsBarChart;
